

import { useState, useCallback, ReactNode } from 'react';
import { StoreContext, Store, mockUsers, mockGroups, mockApps, mockAppRequests, mockOrgSettings } from './index';
import type { AuthUser, User, App, AppRequest, Group, OrgSettings } from '@/types';

export function StoreProvider({ children }: { children: ReactNode }) {
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(null);
  const [users, setUsers] = useState<User[]>(mockUsers);
  const [groups, setGroups] = useState<Group[]>(mockGroups);
  const [apps, setApps] = useState<App[]>(mockApps);
  const [appRequests, setAppRequests] = useState<AppRequest[]>(mockAppRequests);
  const [orgSettings, setOrgSettings] = useState<OrgSettings>(mockOrgSettings);

  // User operations
  const addUser = useCallback((user: User) => {
    setUsers((prev) => [...prev, user]);
  }, []);

  const updateUser = useCallback((id: string, userData: Partial<User>) => {
    setUsers((prev) =>
      prev.map((user) => (user.id === id ? { ...user, ...userData, updatedAt: new Date() } : user))
    );
  }, []);

  const deleteUser = useCallback((id: string) => {
    setUsers((prev) => prev.filter((user) => user.id !== id));
  }, []);

  const deleteUsers = useCallback((ids: string[]) => {
    setUsers((prev) => prev.filter((user) => !ids.includes(user.id)));
  }, []);

  const importUsers = useCallback((newUsers: User[]) => {
    setUsers((prev) => {
      const existingEmails = new Set(prev.map((u) => u.email));
      const uniqueNewUsers = newUsers.filter((u) => !existingEmails.has(u.email));
      return [...prev, ...uniqueNewUsers];
    });
  }, []);

  // Group operations
  const addGroup = useCallback((group: Group) => {
    setGroups((prev) => [...prev, group]);
  }, []);

  const deleteGroup = useCallback((id: string) => {
    setGroups((prev) => prev.filter((group) => group.id !== id));
  }, []);

  // App operations
  const addApp = useCallback((app: App) => {
    setApps((prev) => [...prev, app]);
  }, []);

  const updateApp = useCallback((id: string, appData: Partial<App>) => {
    setApps((prev) =>
      prev.map((app) => (app.id === id ? { ...app, ...appData, updatedAt: new Date() } : app))
    );
  }, []);

  const deleteApp = useCallback((id: string) => {
    setApps((prev) => prev.filter((app) => app.id !== id));
  }, []);

  // App Request operations
  const addAppRequest = useCallback((request: AppRequest) => {
    setAppRequests((prev) => [...prev, request]);
  }, []);

  const updateAppRequest = useCallback((id: string, requestData: Partial<AppRequest>) => {
    setAppRequests((prev) =>
      prev.map((req) => (req.id === id ? { ...req, ...requestData } : req))
    );
  }, []);

  // Org Settings operations
  const updateOrgSettings = useCallback((settings: Partial<OrgSettings>) => {
    setOrgSettings((prev) => ({ ...prev, ...settings, updatedAt: new Date() }));
  }, []);

  const store: Store = {
    currentUser,
    setCurrentUser,
    users,
    addUser,
    updateUser,
    deleteUser,
    deleteUsers,
    importUsers,
    groups,
    addGroup,
    deleteGroup,
    apps,
    addApp,
    updateApp,
    deleteApp,
    appRequests,
    addAppRequest,
    updateAppRequest,
    orgSettings,
    updateOrgSettings,
  };

  return <StoreContext.Provider value={store}>{children}</StoreContext.Provider>;
}
